import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../utils/API";

const removeAllImages = (html = "") => html.replace(/<img[^>]*>/gi, "");
const extractAnchors = (html = "") => {
  const out = [];
  const re = /<a[^>]+href=["']#download:([^"']+)["'][^>]*>(.*?)<\/a>/gi;
  let m;
  while ((m = re.exec(html))) {
    const id = m[1];
    const text = (m[2] || "").replace(/<[^>]*>/g, "").trim() || "Fichier";
    out.push({ id, text });
  }
  return out;
};

const isImageName = (name = "") => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);

const extractFilename = (contentDisposition) => {
  if (!contentDisposition) return null;
  const match =
    contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i) ||
    contentDisposition.match(/filename="?([^"]+)"?/i);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]).replace(/(^"|"$)/g, "");
  } catch {
    return match[1].replace(/(^"|"$)/g, "");
  }
};

export default function MessageRichView({
  html,
  maxThumbs = 12,
}) {
  const cleanedHtml = useMemo(() => removeAllImages(html || ""), [html]);
  const anchors = useMemo(() => extractAnchors(html || ""), [html]);

  const imageLinks = useMemo(
    () => anchors.filter((a) => isImageName(a.text)),
    [anchors]
  );
  const docLinks = useMemo(
    () => anchors.filter((a) => !isImageName(a.text)),
    [anchors]
  );

  const [thumbs, setThumbs] = useState({});
  const [loadingIds, setLoadingIds] = useState({});
  useEffect(() => {
    let mounted = true;

    const load = async (id) => {
      if (thumbs[id] || loadingIds[id]) return;
      try {
        setLoadingIds((p) => ({ ...p, [id]: true }));
        const res = await axiosInstance.get(
          `/candidatsmanagement/downloadfile/${id}?inline=1`,
          { responseType: "blob" }
        );
        const url = URL.createObjectURL(res.data);
        if (!mounted) return;
        setThumbs((p) => ({ ...p, [id]: url }));
      } catch {
      } finally {
        if (mounted) setLoadingIds((p) => ({ ...p, [id]: false }));
      }
    };

    imageLinks.slice(0, maxThumbs).forEach((img) => load(img.id));

    return () => {
      mounted = false;
    };
  }, [imageLinks, maxThumbs]);

  const secureOpenById = async (id, filenameFallback) => {
    const res = await axiosInstance.get(
      `/candidatsmanagement/downloadfile/${id}`,
      { responseType: "blob" }
    );

    const contentType = res.headers?.["content-type"] || "";
    const dispo = res.headers?.["content-disposition"] || "";
    const filename = extractFilename(dispo) || filenameFallback || "download";

    const blob = new Blob([res.data], { type: contentType });
    const blobUrl = URL.createObjectURL(blob);

    const canPreview =
      contentType.startsWith("image/") || contentType === "application/pdf";

    if (canPreview) {
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      return;
    }

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  };

  const onClickCapture = async (e) => {
    const a = e.target?.closest?.("a");
    if (!a) return;

    const href = a.getAttribute("href") || "";
    if (!href.startsWith("#download:")) return;

    e.preventDefault();
    e.stopPropagation();

    const id = href.replace("#download:", "");
    const name = a.textContent?.trim() || "download";
    await secureOpenById(id, name);
  };

  return (
    <div onClickCapture={onClickCapture}>
      <div dangerouslySetInnerHTML={{ __html: cleanedHtml }} />

      {imageLinks.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Images</div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: 12,
            }}
          >
            {imageLinks.map((img) => {
              const src = thumbs[img.id];
              const loading = loadingIds[img.id];

              return (
                <div
                  key={img.id}
                  style={{
                    border: "1px solid #e5e5e5",
                    borderRadius: 10,
                    padding: 10,
                    background: "#fff",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: 120,
                      borderRadius: 8,
                      overflow: "hidden",
                      border: "1px solid #eee",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      background: "#fafafa",
                    }}
                    onClick={() => secureOpenById(img.id, img.text)}
                    title="Clique pour prévisualiser"
                  >
                    {src ? (
                      <img
                        src={src}
                        alt={img.text}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        {loading ? "loading..." : "no preview"}
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: 8 }}>
                    <a href={`#download:${img.id}`} style={{ wordBreak: "break-word" }}>
                      {img.text}
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {docLinks.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Documents</div>
          {docLinks.map((d) => (
            <div key={d.id} style={{ marginBottom: 6 }}>
              <a href={`#download:${d.id}`}>{d.text}</a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
